import ComponentCircleLoader from 'components/Loaders/ComponentCircleLoader';
import DatePicker from 'components/SingleDatePicker/SingleDatePicker';
import { t } from 'i18next';
import moment from 'moment';
import { useEffect, useState } from 'react';
import styled from 'styled-components';
import { PageTitle } from 'styledComponents/common';
import ReactSelectDropdown from 'components/ReactSelectDropdown';
import CircularProgressbar from 'components/CircularProgressbar/CircularProgressbar';
import buildStyles from 'components/CircularProgressbar/buildStyles';
import 'components/CircularProgressbar/styles.css';
import ButtonTextLoader from 'components/Loaders/ButtonTextLoader';
import { ButtonIcon, WhiteButtonWithIcon } from 'styledComponents/buttons';
import excelIcon from 'assets/img/icons/excel-green.svg';
import { exportToExcel } from 'utils/exportToExcel';

// ─── Layout ────────────────────────────────────────────────────────────────────

const PageHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 24px;
  flex-wrap: wrap;
  gap: 12px;
`;

const FiltersRow = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  flex-wrap: wrap;
`;

const FilterItem = styled.div`
  width: 180px;
  flex-shrink: 0;

  .SingleDatePickerInput {
    width: 100% !important;
    height: 40px !important;
    border-radius: 8px !important;
  }
  .SingleDatePicker_picker {
    z-index: 99;
  }
`;

const ExportButtonWrapper = styled.div`
  height: 40px;
  display: flex;
  align-items: stretch;

  button {
    height: 40px;
    min-height: 40px;
    white-space: nowrap;
  }
`;



const BigCard = styled.div`
  background: #fff;
  border-radius: 14px;
  padding: 32px 28px;
  box-shadow: 0 2px 10px rgba(0, 0, 0, 0.08);
`;

const SectionsRow = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr 1fr;
  gap: 0;
  @media (max-width: 900px) {
    grid-template-columns: 1fr;
  }
`;

const Section = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 28px 20px;
  border-right: ${({ last }) => (last ? 'none' : '1.5px solid #f0f0f0')};
  @media (max-width: 900px) {
    border-right: none;
    border-bottom: ${({ last }) => (last ? 'none' : '1.5px solid #f0f0f0')};
  }
`;

const SectionLabel = styled.div`
  font-size: 15px;
  font-weight: 700;
  color: #374151;
  margin-bottom: 20px;
  text-align: center;
`;

const CircleWrapper = styled.div`
  width: 130px;
  height: 130px;
  margin-bottom: 14px;
`;

const CircleInnerText = styled.div`
  font-size: 18px;
  font-weight: 700;
  color: ${({ color }) => color || '#2563eb'};
`;

const FractionLabel = styled.div`
  font-size: 22px;
  font-weight: 700;
  color: #374151;
  margin-top: 4px;
`;

const FractionSub = styled.div`
  font-size: 13px;
  color: #6b7280;
  margin-top: 2px;
`;

const BigNumber = styled.div`
  font-size: 64px;
  font-weight: 800;
  color: #dc2626;
  line-height: 1;
  margin-bottom: 8px;
`;

const BigNumberLabel = styled.div`
  font-size: 14px;
  color: #6b7280;
  font-weight: 500;
`;

// ─── Helpers ───────────────────────────────────────────────────────────────────

const toPercent = (part, total) => {
  if (!total || total === 0) return 0;
  return Math.round((part / total) * 100);
};

const dropdownStyles = {
  container: base => ({
    ...base,
    width: '100%',
  }),
  control: base => ({
    ...base,
    borderRadius: '8px',
    minHeight: '40px',
    height: '40px',
    fontSize: '13px',
    flexWrap: 'nowrap',
  }),
  valueContainer: base => ({
    ...base,
    height: '40px',
    padding: '0 8px',
    flexWrap: 'nowrap',
  }),
  indicatorsContainer: base => ({
    ...base,
    height: '40px',
  }),
  clearIndicator: base => ({
    ...base,
    padding: '0 4px',
  }),
  dropdownIndicator: base => ({
    ...base,
    padding: '0 6px',
  }),
  input: base => ({
    ...base,
    margin: 0,
    padding: 0,
  }),
  singleValue: base => ({
    ...base,
    fontSize: '13px',
  }),
};

// ─── Component ─────────────────────────────────────────────────────────────────

const SuspicionDashboard = ({
  getSuspicionDashboard,
  getSuspicionMembers,
  getOfficesShortList,
  isLoading,
  dashboardData,
  membersLoading,
  membersList,
  officesShortList,
}) => {
  const [selectedDate, setSelectedDate] = useState(moment());
  const [selectedOffice, setSelectedOffice] = useState(null);
  const [selectedUser, setSelectedUser] = useState(null);

  useEffect(() => {
    getOfficesShortList();
    fetchDashboard({ date: moment().format('YYYY-MM-DD') });
  }, []);

  const fetchDashboard = ({ date, office_id, user_id }) => {
    getSuspicionDashboard({ date, office_id, user_id });
  };

  const handleDateChange = date => {
    setSelectedDate(date);
    fetchDashboard({
      date: date ? date.format('YYYY-MM-DD') : moment().format('YYYY-MM-DD'),
      office_id: selectedOffice?.value,
      user_id: selectedUser?.value,
    });
  };

  const handleOfficeChange = option => {
    setSelectedOffice(option);
    setSelectedUser(null);
    if (option) {
      getSuspicionMembers({ office_id: option.value });
    }
    fetchDashboard({
      date: selectedDate ? selectedDate.format('YYYY-MM-DD') : moment().format('YYYY-MM-DD'),
      office_id: option?.value,
      user_id: undefined,
    });
  };

  const handleUserChange = option => {
    setSelectedUser(option);
    fetchDashboard({
      date: selectedDate ? selectedDate.format('YYYY-MM-DD') : moment().format('YYYY-MM-DD'),
      office_id: selectedOffice?.value,
      user_id: option?.value,
    });
  };

  const officeOptions = (officesShortList || []).map(o => ({
    value: o.id,
    label: o.name,
  }));

  const memberOptions = (membersList || []).map(m => ({
    value: m.id,
    label: m.name || m.full_name || `${m.first_name || ''} ${m.last_name || ''}`.trim(),
  }));

  const [exportLoading, setExportLoading] = useState(false);

  const handleExport = async () => {
    const date = selectedDate ? selectedDate.format('YYYY-MM-DD') : moment().format('YYYY-MM-DD');
    const params = new URLSearchParams({ date });
    if (selectedUser?.value) params.append('user_id', selectedUser.value);
    if (selectedOffice?.value) params.append('office_id', selectedOffice.value);
    const url = `albait/export/mock-location-report/?${params.toString()}`;
    const fileName = `mock_location_report_${date}.xlsx`;
    try {
      setExportLoading(true);
      await exportToExcel(url, fileName, true);
    } catch (err) {
      console.error('Export failed', err);
    } finally {
      setExportLoading(false);
    }
  };

  const officePercent = toPercent(dashboardData?.distinct_offices, dashboardData?.total_offices);
  const userPercent = toPercent(dashboardData?.distinct_users, dashboardData?.total_users);
  const d = dashboardData || {};

  return (
    <div className="content">
      <PageHeader>
        <PageTitle style={{ marginBottom: 0 }}>{t('suspicion_dashboard')}</PageTitle>
        <FiltersRow>
          <FilterItem>
            <DatePicker
              date={selectedDate}
              onDateChange={handleDateChange}
              dateDisplayFormat="DD/MM/YYYY"
              placeholder={t('select_date')}
              allowPreviousDates
              disableFutureDates
            />
          </FilterItem>

          <FilterItem>
            <ReactSelectDropdown
              value={selectedOffice}
              onChange={handleOfficeChange}
              options={officeOptions}
              placeholder={t('all_offices')}
              isClearable
              styles={dropdownStyles}
            />
          </FilterItem>

          <FilterItem>
            <ReactSelectDropdown
              value={selectedUser}
              onChange={handleUserChange}
              options={memberOptions}
              placeholder={t('select_assignee')}
              isClearable
              isDisabled={!selectedOffice}
              isLoading={membersLoading}
              styles={dropdownStyles}
            />
          </FilterItem>

          <ExportButtonWrapper>
            <WhiteButtonWithIcon
              onClick={handleExport}
              color="#19AC65"
              borderColor="#19AC65"
              hoverBorderColor="#129652"
              padding={exportLoading ? '0 45px' : '0 25px'}
              disabled={exportLoading}
              style={exportLoading ? { opacity: 0.5, cursor: 'not-allowed' } : {}}
            >
              {exportLoading ? (
                <ButtonTextLoader color="#19AC65" loadingText=" " size={6} />
              ) : (
                <>
                  <ButtonIcon src={excelIcon} size="26px" />
                  {t('export_report')}
                </>
              )}
            </WhiteButtonWithIcon>
          </ExportButtonWrapper>
        </FiltersRow>
      </PageHeader>

      {isLoading ? (
        <ComponentCircleLoader padding="10vh" />
      ) : (
        <BigCard>
          <SectionsRow>
            {/* ── Total Suspected Offices ── */}
            <Section>
              <SectionLabel>{t('total_suspected_offices')}</SectionLabel>
              <CircleWrapper>
                <CircularProgressbar
                  value={officePercent}
                  maxValue={100}
                  text={`${officePercent}%`}
                  styles={buildStyles({
                    pathColor: '#7c3aed',
                    trailColor: '#ede9fe',
                    textColor: '#7c3aed',
                    textSize: '18px',
                  })}
                />
              </CircleWrapper>
              <FractionLabel>
                {d.distinct_offices ?? 0} / {d.total_offices ?? 0}
              </FractionLabel>
              <FractionSub>{t('suspected_out_of_total_offices')}</FractionSub>
            </Section>

            {/* ── Total Suspected Users ── */}
            <Section>
              <SectionLabel>{t('total_suspected_users')}</SectionLabel>
              <CircleWrapper>
                <CircularProgressbar
                  value={userPercent}
                  maxValue={100}
                  text={`${userPercent}%`}
                  styles={buildStyles({
                    pathColor: '#dc2626',
                    trailColor: '#fee2e2',
                    textColor: '#dc2626',
                    textSize: '18px',
                  })}
                />
              </CircleWrapper>
              <FractionLabel>
                {d.distinct_users ?? 0} / {d.total_users ?? 0}
              </FractionLabel>
              <FractionSub>{t('suspected_out_of_total_users')}</FractionSub>
            </Section>

            {/* ── Total Suspected Detections ── */}
            <Section last>
              <SectionLabel>{t('total_suspected_detections')}</SectionLabel>
              <BigNumber>{d.total_mock_locations ?? 0}</BigNumber>
              <BigNumberLabel>{t('total_mock_location_detections')}</BigNumberLabel>
            </Section>
          </SectionsRow>
        </BigCard>
      )}
    </div>
  );
};

export default SuspicionDashboard;
